import { Button, Card, Col, Container, Row, Stack } from "react-bootstrap";
import { DynamicText } from "@/ui/DynamicText";
import JobSearch from "@sourceflow-uk/job-search";
import "@sourceflow-uk/job-search/dist/bundle.css";
import { trimText } from "@/functions/trimText";
import { useLocale } from "@/hooks/useLocale";
import { placeholders } from "@/data/placeholders";
import classes from "./styles.module.scss";
import clsx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLink } from "@/hooks/useLink";
import Reset from "@/assets/Reset.svg";
import jobsSectors from "@/../.sourceflow/jobs-Sectors.json";
import jobsType from "@/../.sourceflow/jobs-Type.json";
import jobsWorkingEnvironment from "@/../.sourceflow/jobs-Working Environment.json";
import CaretDown from "@/assets/CaretDown.svg";

export const JobsPageFeedSection = ({ className }) => {
  const [seconds, setSeconds] = useState(5);
  const locale = useLocale();
  const link = useLink();
  const insertRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [allowRedirect, setAllowRedirect] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);

      if (params.has("redirect") && params.get("redirect") === "0") {
        setAllowRedirect(false);
      }
    }
  }, []);

  useEffect(() => {
    const wrapper = document.getElementsByClassName("js-form-input-wrapper").item(0);
    if (wrapper && insertRef.current === false) {
      const input1 = wrapper.children[0];
      const input2 = wrapper.children[1];
      const input3 = wrapper.children[2];
      const button = wrapper.children[3];
      const div1 = document.createElement("div");
      const div2 = document.createElement("div");
      const div3 = document.createElement("div");
      const label1 = document.createElement("label");
      const label2 = document.createElement("label");
      const label3 = document.createElement("label");
      label1.innerText = placeholders[`jobs.search.query.label.${locale}`];
      label2.innerText = placeholders[`jobs.search.location.label.${locale}`];
      label3.innerText = placeholders[`jobs.search.radius.label.${locale}`];
      div1.append(label1, input1);
      div2.append(label2, input2);
      div3.append(label3, input3);
      wrapper.replaceChildren(div1, div2, div3, button);
      insertRef.current = true;
    }
  }, [locale]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setReady(true);
    }, 1500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const redirectAfterDelay = useCallback(() => {
    const interval = setTimeout(() => {
      if (allowRedirect === true) {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        } else {
          const urlParams = new URLSearchParams({ redirect: 0 });
          window.location.href = `${window.location.pathname}?${urlParams.toString()}`;
        }
      }
    }, 1000);

    return () => {
      clearTimeout(interval);
    };
  }, [seconds, allowRedirect]);

  return (
    <div className={clsx(className, classes.root, { loading: !ready })}>
      <Container className="px-2 px-md-3">
        <JobSearch
          translations={{
            "button.text": placeholders[`jobs.search.button.${locale}`],
            "input.placeHolder": placeholders[`jobs.search.query.placeholder.${locale}`],
            "input.locationPlaceholder": placeholders[`jobs.search.location.placeholder.${locale}`],
            "searchResults.noResultsText": placeholders[`jobs.search.noResults.${locale}`],
            "filters.resetSearchText": (
              <>
                <Reset />
                <span className="ms-2">{placeholders[`jobs.search.resetSearch.${locale}`]}</span>
              </>
            ),
          }}
          options={{
            // searchPagination: {
            //   variant: "paged",
            //   reactPaginationOptions: {
            //     pageRangeDisplayed: 3,
            //     marginPagesDisplayed: 4,
            //     nextLabel: "next >",
            //     previousLabel: "< prev",
            //   },
            // },
            urlFilters: {
              mode: "active",
              jobCategories: [jobsSectors, jobsType, jobsWorkingEnvironment],
              pushToHistoryOnSearch: true,
              changeURLOnFilterChange: true,
            },
            jobsUrl: `/jobs`,
            searchInput: {
              queryInput: true,
              locationInput: true,
              locationRegion: "",
              radiusInput: true,
              radiusOptions: {
                units: "kilometres",
              },
            },
            searchFiltersOptions: {
              filterFoldElement: <CaretDown width={20} height={20} />,
              showResetSearchButton: true,
              topLevelCategoryOrder: [jobsSectors.id, jobsType.id, jobsWorkingEnvironment.id],
              nestedCategories: [jobsSectors],
              filterFoldDepth: 99,
              allowFilterCollapse: true,
            },
            searchResults: ({ results }) => {
              if (!results || results.length === 0) {
                redirectAfterDelay();

                return (
                  <Row>
                    <div className="js-result-none">
                      {allowRedirect
                        ? placeholders[`jobs.search.noResults.withRedirect.${locale}`].replaceAll("{seconds}", seconds)
                        : placeholders[`jobs.search.noResults.${locale}`]}
                    </div>
                  </Row>
                );
              }

              return (
                <Row>
                  {results.map(({ job }, k) => (
                    <Col xs={12} key={job.id} className="mb-4">
                      <Card className={clsx(classes.card, "bg-light")}>
                        <Card.Header>
                          {k < 4 && <i>{placeholders[`job.card.new.${locale}`]}</i>}
                          <Card.Title className="mb-3" dangerouslySetInnerHTML={{ __html: job.title }} />
                          <Stack className="flex-md-row" gap={{ md: 5, xs: 2 }}>
                            <dl className="d-flex flex-row mb-0">
                              <dt className="me-2">
                                <DynamicText path={`job.card.salary.label.${locale}`} />:
                              </dt>
                              <dd className="mb-0" dangerouslySetInnerHTML={{ __html: job.salary_package }} />
                            </dl>
                            <dl className="d-flex flex-row mb-0">
                              <dt className="me-2">
                                <DynamicText path={`job.card.location.label.${locale}`} />:
                              </dt>
                              <dd className="mb-0" dangerouslySetInnerHTML={{ __html: job.derived_info?.locations?.[0].postal_address?.address_lines[0] ?? job.addresses[0] }} />
                            </dl>
                          </Stack>
                        </Card.Header>
                        <Card.Body>
                          <Card.Text dangerouslySetInnerHTML={{ __html: trimText(job.description, 148) }} />
                        </Card.Body>
                        <Card.Footer className="d-flex flex-row justify-content-between align-items-center">
                          <a href={link("jobs.item", job.url_slug, "#apply")}>
                            <DynamicText path={`job.card.apply.${locale}`} />
                          </a>
                          <Button variant="outline-primary" href={link("jobs.item", job.url_slug)}>
                            <DynamicText path={`job.card.button.${locale}`} />
                          </Button>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>
              );
            },
          }}
        />
      </Container>
    </div>
  );
};
